import { Link } from '@tanstack/react-router';
import { format, isToday, isYesterday } from 'date-fns';
import type { AgentApiDashboardsActivity } from '@/types/serializers';
import DashboardEmptyState from './DashboardEmptyState';

type Props = {
  activities: AgentApiDashboardsActivity[];
  isLoading: boolean;
};

/** 発生時刻の表示（本日は時刻、昨日は「昨日」、それ以前は M/D） */
function formatActedAt(actedAt: string): string {
  const date = new Date(actedAt);
  if (Number.isNaN(date.getTime())) return '';
  if (isToday(date)) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
  if (isYesterday(date)) return '昨日';
  return format(date, 'M/d');
}

/**
 * 候補者の応募・フォーム回答アクティビティを時系列降順で表示する。
 * - 応募: 「{候補者} さんが {企業} に応募しました。」
 * - フォーム回答: 「{候補者} さんがフォームに回答しました。」
 */
export default function DashboardActivityInfo({ activities, isLoading }: Props) {
  return (
    <div className="flex flex-col gap-xs rounded-sm bg-white p-md shadow-[0_0_3px_0_rgba(0,0,0,0.2)] xl:min-h-0 xl:flex-1">
      <h2 className="text-sm leading-normal font-bold text-[#101828]">アップデート情報</h2>

      {isLoading ? (
        <ul className="flex h-[284px] flex-col px-sm xl:h-auto xl:min-h-0 xl:flex-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <li
              key={i}
              className="flex items-center gap-md border-b border-solid border-border px-xs py-md last:border-b-0"
            >
              <span className="h-5 w-[38px] shrink-0 animate-pulse rounded-sm bg-medium-gray" />
              <span className="h-5 flex-1 animate-pulse rounded-sm bg-medium-gray" />
            </li>
          ))}
        </ul>
      ) : activities.length === 0 ? (
        <DashboardEmptyState className="min-h-[100px] xl:min-h-0 xl:flex-1" />
      ) : (
        <div className="hidden-scrollbar flex h-[284px] flex-col overflow-y-auto rounded-sm border border-solid border-border bg-white px-sm xl:h-auto xl:min-h-0 xl:flex-1">
          <ul className="flex flex-col">
            {activities.map((activity, index) => (
              <li
                key={index}
                className="flex items-center gap-md border-b border-solid border-border px-xs py-md last:border-b-0"
              >
                <span className="w-[38px] shrink-0 text-sm leading-normal whitespace-nowrap text-main">
                  {formatActedAt(activity.acted_at)}
                </span>
                <p className="min-w-0 flex-1 text-xs leading-[1.7] text-main">
                  <Link to={activity.candidate_url} className="text-link">
                    {activity.candidate_name}
                  </Link>
                  <span>さんが</span>
                  {activity.kind === 'application' ? (
                    <>
                      {activity.company_url && activity.company_name ? (
                        <Link to={activity.company_url} className="text-link">
                          {activity.company_name}
                        </Link>
                      ) : (
                        <span className="text-link">{activity.company_name}</span>
                      )}
                      <span>に応募しました。</span>
                    </>
                  ) : (
                    <span>フォームに回答しました。</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
